import axios from 'utils/axios';

const SETTINGS_PATH = 'api/settings';
const FAQ_PATH = 'api/settings/faq';

/** Tab keys used by CMS UI — map to API fields on `GET/PUT api/settings`. */
export type CmsPageType = 'terms_conditions' | 'privacy_policy' | 'faq' | 'about_us';

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  isActive?: boolean;
}

export interface CreateFaqPayload {
  question: string;
  answer: string;
}

export interface UpdateFaqPayload {
  question: string;
  answer: string;
}

export interface CmsPageUpdatePayload {
  content?: string;
  title?: string;
}

export interface CmsPageModel {
  id: string | null;
  content: string;
}

interface SettingsFaqRow {
  _id?: string;
  id?: string;
  question: string;
  answer: string;
}

/** FAQ uuid from API (`id` preferred, `_id` for legacy Mongo shape). */
export function faqRowId(row: Pick<SettingsFaqRow, 'id' | '_id'>): string {
  const raw = row.id ?? row._id;
  return typeof raw === 'string' ? raw.trim() : '';
}

export function faqItemsFromSettingsDoc(doc: SettingsApiData): FaqItem[] {
  return (doc.faq ?? []).map((row) => ({
    id: faqRowId(row),
    question: row.question,
    answer: row.answer,
    isActive: true,
  }));
}

/** Outgoing FAQ rows: existing entries keep `_id`, new entries omit it. */
type FaqWriteRow = { _id?: string; question: string; answer: string };

export interface SettingsApiData {
  _id: string;
  terms?: string;
  privacyPolicy?: string;
  about?: string;
  faq?: SettingsFaqRow[];
  createdAt?: string;
  updatedAt?: string;
}

interface SettingsApiEnvelope {
  success?: boolean;
  message?: string;
  data: SettingsApiData;
}

function unwrapSettingsPayload(res: unknown): SettingsApiData {
  if (res && typeof res === 'object' && 'data' in res) {
    const inner = (res as SettingsApiEnvelope).data;
    if (inner && typeof inner === 'object') return inner;
  }
  return res as SettingsApiData;
}

async function fetchSettingsDoc(): Promise<SettingsApiData> {
  const res = await axios.get<SettingsApiEnvelope | SettingsApiData>(SETTINGS_PATH);
  const doc = unwrapSettingsPayload(res.data);
  if (!doc || typeof doc !== 'object') {
    throw new Error('Invalid settings response');
  }
  return doc;
}

/** Single GET of the combined settings document (terms, privacy, about, FAQ). */
export async function getSettingsDocument(): Promise<SettingsApiData> {
  return fetchSettingsDoc();
}

/** Full document replace merge — read current, patch fields, PUT body the API expects. */
async function mergeAndPut(
  patch: Partial<Pick<SettingsApiData, 'terms' | 'privacyPolicy' | 'about'>> & { faq?: FaqWriteRow[] }
): Promise<SettingsApiData> {
  const cur = await fetchSettingsDoc();
  const faq: FaqWriteRow[] =
    patch.faq ??
    (cur.faq ?? []).map((r) => {
      const id = faqRowId(r);
      return { ...(id ? { _id: id } : {}), question: r.question, answer: r.answer };
    });
  const body = {
    terms: patch.terms ?? cur.terms ?? '',
    privacyPolicy: patch.privacyPolicy ?? cur.privacyPolicy ?? '',
    about: patch.about ?? cur.about ?? '',
    faq,
  };
  const res = await axios.put<SettingsApiEnvelope | SettingsApiData>(SETTINGS_PATH, body);
  return unwrapSettingsPayload(res.data);
}

export function pageModelFromSettingsDoc(doc: SettingsApiData, pageType: CmsPageType): CmsPageModel {
  const id = doc._id ?? null;
  switch (pageType) {
    case 'terms_conditions':
      return { id, content: doc.terms ?? '' };
    case 'privacy_policy':
      return { id, content: doc.privacyPolicy ?? '' };
    case 'about_us':
      return { id, content: doc.about ?? '' };
    default:
      return { id, content: '' };
  }
}

async function getPage(pageType: CmsPageType): Promise<CmsPageModel> {
  const doc = await fetchSettingsDoc();
  return pageModelFromSettingsDoc(doc, pageType);
}

async function updatePage(
  pageType: CmsPageType,
  payload: CmsPageUpdatePayload,
  _options?: { pageId?: string | null }
): Promise<SettingsApiData> {
  if (pageType === 'faq') {
    throw new Error('FAQ content is updated via FAQ APIs');
  }
  const content = payload.content ?? '';

  if (pageType === 'terms_conditions') {
    return mergeAndPut({ terms: content });
  }
  if (pageType === 'privacy_policy') {
    return mergeAndPut({ privacyPolicy: content });
  }
  if (pageType === 'about_us') {
    return mergeAndPut({ about: content });
  }
  return fetchSettingsDoc();
}

export interface DeleteCmsPageTarget {
  id: string;
  pageType: CmsPageType;
}

async function deletePage(target: DeleteCmsPageTarget): Promise<SettingsApiData> {
  const { pageType } = target;
  if (pageType === 'terms_conditions') {
    return mergeAndPut({ terms: '' });
  }
  if (pageType === 'privacy_policy') {
    return mergeAndPut({ privacyPolicy: '' });
  }
  if (pageType === 'about_us') {
    return mergeAndPut({ about: '' });
  }
  throw new Error('Delete is only supported for terms, privacy policy, and about');
}

async function listAllFaqs(): Promise<FaqItem[]> {
  const doc = await fetchSettingsDoc();
  return faqItemsFromSettingsDoc(doc);
}

async function refreshSettingsAfterFaqMutation(res: unknown): Promise<SettingsApiData> {
  const doc = unwrapSettingsPayload(res);
  if (doc?.faq) return doc;
  return fetchSettingsDoc();
}

async function createFaq(payload: CreateFaqPayload): Promise<SettingsApiData> {
  const cur = await fetchSettingsDoc();
  const existing: FaqWriteRow[] = (cur.faq ?? []).map((r) => {
    const id = faqRowId(r);
    return { ...(id ? { _id: id } : {}), question: r.question, answer: r.answer };
  });
  return mergeAndPut({
    faq: [...existing, { question: payload.question.trim(), answer: payload.answer.trim() }],
  });
}

async function updateFaq(faqId: string, payload: UpdateFaqPayload): Promise<SettingsApiData> {
  const res = await axios.patch<SettingsApiEnvelope | SettingsApiData>(FAQ_PATH, {
    id: faqId,
    question: payload.question.trim(),
    answer: payload.answer.trim(),
  });
  return refreshSettingsAfterFaqMutation(res.data);
}

async function deleteFaq(faqId: string): Promise<SettingsApiData> {
  const res = await axios.delete<SettingsApiEnvelope | SettingsApiData>(FAQ_PATH, {
    data: { id: faqId },
  });
  return refreshSettingsAfterFaqMutation(res.data);
}

const cmsService = {
  getPage,
  updatePage,
  deletePage,
  listAllFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
};

export default cmsService;
